import React, { useState, useEffect, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from '@material-tailwind/react';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import {
  ReactPlugin,
  AppInsightsContext
} from '@microsoft/applicationinsights-react-js';
import App from './App';
import SpinnerPage from './components/SpinnerPage';
import { getAppInsightsConnStr } from './apis/Config';

function AppInitializer() {
  const [isInitialized, setIsInitialized] = useState(false);
  const reactPluginRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    async function initialize() {
      let appInsights = null;

      try {
        let connStr = await getAppInsightsConnStr();

        if (connStr && isMounted) {
          const plugin = new ReactPlugin();
          reactPluginRef.current = plugin;

          appInsights = new ApplicationInsights({
            config: {
              connectionString: '',
              instrumentationKey: connStr,
              extensions: [plugin],
              enableAutoRouteTracking: true,
              disableAjaxTracking: false,
              enableAjaxPerfTracking: true,
              enableRequestHeaderTracking: true,
              enableResponseHeaderTracking: true,
              disableFetchTracking: false,
              enableCorsCorrelation: true
            }
          });

          appInsights.addTelemetryInitializer((envelope) => {
            const excludedEndpoints = ['/Account/CheckSession'];

            const baseData = envelope.baseData || {};

            if (
              envelope.baseType &&
              envelope.baseType.indexOf('RemoteDependencyData') !== -1
            ) {
              const dependencyName = baseData.name || '';
              const dependencyTarget = baseData.target || '';
              const dependencyData = baseData.data || '';

              for (const excluded of excludedEndpoints) {
                if (
                  dependencyName.includes(excluded) ||
                  dependencyTarget.includes(excluded) ||
                  dependencyData.includes(excluded)
                ) {
                  return false;
                }
              }
            }

            return true;
          });

          appInsights.loadAppInsights();

          appInsights.addTelemetryInitializer((envelope) => {
              const baseData = envelope.baseData || {};
              baseData.properties = baseData.properties || {};

              baseData.properties['environment'] = process.env.NODE_ENV || 'unknown';
              baseData.properties['portal'] = '';
              baseData.properties['clientId'] = '';
              baseData.properties['sessionId'] = '';

              return true;
          });

        } else if (isMounted) {
          console.warn(
            'Application Insights connection setup issue. Continuing without Application Insights...'
          );
        }
      } catch (ex) {
        console.error(`App Insights initialization failed`);
      } finally {
        if (isMounted) {
          setIsInitialized(true);
        }
      }
    }

    initialize();

    return () => {
      isMounted = false;
    };
  }, []);

  if (!isInitialized) {
    return <div>{<SpinnerPage></SpinnerPage>}</div>;
  }

  return (
    <AppInsightsContext.Provider value={reactPluginRef.current}>
      <BrowserRouter>
        <ThemeProvider>
          <App />
          <ToastContainer
            position='top-right'
            // autoClose={5000}

            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            // pauseOnFocusLoss
            draggable
            pauseOnHover
            containerStyle={{
              zIndex: '100000 !important'
            }}
            toastOptions={{
              className: 'react-hot-toast',
              style: {
                zIndex: '99999 !important'
              }
            }}
          />
        </ThemeProvider>
      </BrowserRouter>
    </AppInsightsContext.Provider>
  );
}

export default AppInitializer;
